import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Lock } from "lucide-react";

let _cache = null;
let _cachePromise = null;

async function _loadActiveSKUs() {
  if (_cache) return _cache;
  if (_cachePromise) return _cachePromise;
  _cachePromise = api.get("/sku-master", { params: { active_only: true } })
    .then(({ data }) => { _cache = data || []; _cachePromise = null; return _cache; })
    .catch(() => { _cachePromise = null; return []; });
  return _cachePromise;
}

/** Invalidate the in-memory SKU cache. Called after SKU Master mutations
 *  (create / patch / delete / bulk-apply) so dropdowns pick up new entries
 *  without a hard refresh. */
export function invalidateSKUCache() {
  _cache = null; _cachePromise = null;
}

/**
 * Searchable SKU dropdown sourced from /api/sku-master?active_only=true.
 * - `value`           current sku_id string
 * - `onChange(sku_id)` fires when user picks a new SKU
 * - `disabled`        force read-only (e.g. existing record where SKU is immutable)
 * - `legacyAllowed`   when true and `value` isn't found in the master list,
 *                     the field is rendered READ-ONLY with a "legacy" badge —
 *                     prevents data loss on rows imported before the master existed.
 * - `placeholder`     placeholder text
 * - `testId`          base data-testid; "-trigger" / "-search" / "-option-{sku}" suffixes added.
 */
export function SKUMasterCombobox({ value, onChange, disabled = false, legacyAllowed = true, placeholder = "Select SKU…", testId = "sku-combo" }) {
  const [open, setOpen] = useState(false);
  const [skus, setSkus] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    _loadActiveSKUs().then((rows) => { if (alive) { setSkus(rows); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  const found = useMemo(() => skus.find((s) => s.sku_id === value), [skus, value]);
  const isLegacy = !!value && loaded && !found;

  // Read-only branch: existing record whose SKU isn't in the master.
  if (isLegacy && legacyAllowed) {
    return (
      <div className="flex items-center gap-2" data-testid={`${testId}-legacy`}>
        <div className="flex-1 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-sm text-slate-900 mono">{value}</div>
        <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-100 border border-amber-200 px-2 py-1 rounded-full">
          <Lock className="h-3 w-3" /> legacy
        </span>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal mono text-[13px]"
          data-testid={`${testId}-trigger`}
        >
          {found ? (
            <span className="truncate"><span className="font-semibold">{found.sku_id}</span> <span className="text-slate-500 ml-2">{found.sku_name}</span></span>
          ) : value ? (
            <span className="text-slate-900">{value}</span>
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search SKU ID or name…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>{loaded ? "No SKU found. Add it via SKU Master first." : "Loading…"}</CommandEmpty>
            <CommandGroup>
              {skus.map((s) => (
                <CommandItem
                  key={s.sku_id}
                  value={`${s.sku_id} ${s.sku_name}`}
                  onSelect={() => { onChange?.(s.sku_id); setOpen(false); }}
                  data-testid={`${testId}-option-${s.sku_id}`}
                  className="cursor-pointer"
                >
                  <Check className={`mr-2 h-4 w-4 ${value === s.sku_id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="mono text-[12px] font-semibold text-slate-900">{s.sku_id}</div>
                    <div className="text-[11px] text-slate-500 truncate">{s.sku_name}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
